<template>
  <div class="report-modal">
    <h1 class="report-modal__title">
      {{ $t('session.reportSessionModal.title') }}
    </h1>
    <h2 class="report-modal__subtitle">
      {{ reportModalSubtitle }}
      <a
        v-if="!isInRecap"
        href="https://upc-training-materials.s3.us-east-2.amazonaws.com/reporting-guidelines.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ $t('session.reportSessionModal.subtitle') }}
      </a>
    </h2>

    <div class="report-modal__section">
      <div class="report-modal__label">
        {{ $t('session.reportSessionModal.reportReasonLabel') }}
      </div>
      <v-select
        class="report-modal__select report-modal__select--required"
        v-model="reportReason"
        :options="reportReasonOptionsToDisplay"
        :searchable="false"
        :clearable="false"
      ></v-select>
    </div>
    <div class="report-modal__section">
      <div class="report-modal__label">
        {{ $t('session.reportSessionModal.reportMessageLabel') }}
      </div>
      <textarea
        class="report-modal__message"
        v-model="reportMessage"
        :placeholder="$t('session.reportSessionModal.reportMessagePlaceholder')"
        rows="3"
      />
    </div>
    <div class="report-modal__footer">
      <div class="report-modal__buttons">
        <large-button @click.native="cancel">{{
          $t('general.cancel')
        }}</large-button>
        <large-button
          primary
          @click.native="submit"
          :disabled="!isFormComplete"
          >{{ $t('session.reportSessionModal.report') }}</large-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import NetworkService from '@/services/NetworkService'
import AnalyticsService from '@/services/AnalyticsService'
import LargeButton from '@/components/LargeButton.vue'
import { mapState } from 'vuex'
import { EVENTS } from '@/consts'

export default {
  components: { LargeButton },
  props: {
    modalData: { type: Object, required: true },
  },
  data() {
    return {
      // TODO: Options should be hydrated via the database
      reportReasonOptions: [
        this.$root.$t(
          'session.reportSessionModal.reportReasonOptions.option-1'
        ),
        this.$root.$t(
          'session.reportSessionModal.reportReasonOptions.option-2'
        ),
      ],
      reportReason: null,
      reportMessage: '',
    }
  },
  mounted() {
    if (this.isInRecap)
      AnalyticsService.captureEvent(
        EVENTS.SESSION_RECAP_DM_REPORT_MODAL_SHOWN,
        {
          sessionId: this.currentSession.id,
        }
      )
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    isFormComplete() {
      return !!this.reportReason
    },
    currentSession() {
      return this.modalData.currentSession
    },
    isInRecap() {
      return this.modalData.isInRecap ?? false
    },
    reportReasonOptionsToDisplay() {
      const studentRecapReportReasonOptions = [
        this.$root.$t(
          'session.reportSessionModal.studentRecapReportReasonOptions.option-1'
        ),
        this.$root.$t(
          'session.reportSessionModal.studentRecapReportReasonOptions.option-2'
        ),
        this.$root.$t(
          'session.reportSessionModal.studentRecapReportReasonOptions.option-3'
        ),
        this.$root.$t(
          'session.reportSessionModal.studentRecapReportReasonOptions.option-4'
        ),
      ]
      if (!this.isInRecap) return this.reportReasonOptions
      else {
        if (this.user.isVolunteer) return this.reportReasonOptions
        else return studentRecapReportReasonOptions
      }
    },
    reportModalSubtitle() {
      if (this.isInRecap && !this.user.isVolunteer)
        return this.$root.$t(
          'session.reportSessionModal.isNotVolunteerSubtitle'
        )
      else
        return this.user.isVolunteer
          ? this.$root.$t('session.reportSessionModal.studentsSubtitle')
          : this.$root.$t('session.reportSessionModal.tutorsSubtitle')
    },
  },
  methods: {
    async submit() {
      try {
        await NetworkService.reportSession({
          sessionId: this.currentSession.id,
          reportReason: this.reportReason,
          reportMessage: this.reportMessage,
          source: this.isInRecap ? 'recap' : '',
        })
        if (this.modalData.toggleReportSubmitted)
          this.modalData.toggleReportSubmitted()
      } catch (error) {
        alert(this.$root.$t('session.reportSessionModal.alertText'))
      }

      if (this.isInRecap)
        AnalyticsService.captureEvent(
          EVENTS.SESSION_RECAP_DM_REPORT_MODAL_SUBMITTED,
          {
            sessionId: this.currentSession.id,
          }
        )

      this.$store.dispatch('app/modal/hide')
    },
    cancel() {
      this.$store.dispatch('app/modal/hide')
    },
  },
}
</script>

<style lang="scss" scoped>
.report-modal {
  @include flex-container(column);

  &__title {
    @include font-category('display-small');
  }

  &__subtitle {
    @include font-category('body');
    margin: 0 0 35px;
    color: $c-secondary-grey;
    font-size: 15px;
  }

  &__section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;

    &:not(:last-of-type) {
      margin-bottom: 20px;
    }
  }

  &__label {
    align-self: flex-start;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 5px;
  }

  &__message {
    padding: 10px;
    border: solid 1px $c-border-grey;
    border-radius: 5px;
    resize: none;

    &:focus {
      outline: none;
    }
  }

  &__buttons {
    margin-top: 16px;
    @include flex-container(row, flex-end);
    @include child-spacing(left, 16px);
  }
}

html.is-rtl {
  .report-modal {
    &__buttons {
      @include child-spacing-rtl(left, 16px);
    }
  }
}
</style>
